import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import Api from "../../../../shared/api/api";

import "../../styles/DevelopmentAdmin.css";
import "../styles/DevelopmentProjects.css";

const EMPTY_FORM = {
  client_id: "",
  title: "",
  description: "",
  project_type: "website",
  status: "draft",
  priority: "normal",
  start_date: "",
  target_completion_date: "",
  actual_completion_date: "",
  total_cost: "",
  deposit_percentage: "30",
  deposit_paid: false,
  remaining_paid: false,
  internal_notes: "",
  portfolio_eligible: false,
  active: true,
  archived: false,
};

const STATUS_OPTIONS = [
  "draft",
  "planning",
  "active",
  "on_hold",
  "completed",
  "cancelled",
];

const PRIORITY_OPTIONS = [
  "low",
  "normal",
  "high",
  "urgent",
];

const PROJECT_TYPE_OPTIONS = [
  "website",
  "website_redesign",
  "web_application",
  "ecommerce",
  "automation",
  "maintenance",
  "consulting",
  "other",
];

function safeString(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return "";
  }

  return String(value);
}

function cleanText(value) {
  return safeString(value).trim();
}

function optionalText(value) {
  const cleaned = cleanText(value);

  return cleaned || null;
}

function normalizeDateInput(value) {
  if (!value) {
    return "";
  }

  return safeString(value).slice(0, 10);
}

function normalizeProject(project = {}) {
  return {
    client_id:
      project.client_id === null ||
      project.client_id === undefined
        ? ""
        : String(project.client_id),

    title:
      safeString(project.title),

    description:
      safeString(project.description),

    project_type:
      safeString(project.project_type) ||
      "website",

    status:
      safeString(project.status) ||
      "draft",

    priority:
      safeString(project.priority) ||
      "normal",

    start_date:
      normalizeDateInput(
        project.start_date
      ),

    target_completion_date:
      normalizeDateInput(
        project.target_completion_date
      ),

    actual_completion_date:
      normalizeDateInput(
        project.actual_completion_date
      ),

    total_cost:
      project.total_cost === null ||
      project.total_cost === undefined
        ? ""
        : String(project.total_cost),

    deposit_percentage:
      project.deposit_percentage === null ||
      project.deposit_percentage === undefined
        ? "30"
        : String(
            project.deposit_percentage
          ),

    deposit_paid:
      Number(project.deposit_paid) === 1,

    remaining_paid:
      Number(project.remaining_paid) === 1,

    internal_notes:
      safeString(
        project.internal_notes
      ),

    portfolio_eligible:
      Number(
        project.portfolio_eligible
      ) === 1,

    active:
      Number(project.active) === 1,

    archived:
      Number(project.archived) === 1,
  };
}

function formatCurrency(value) {
  const number = Number(value);

  return new Intl.NumberFormat(
    "en-US",
    {
      style: "currency",
      currency: "USD",
    }
  ).format(
    Number.isFinite(number)
      ? number
      : 0
  );
}

function formatDate(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(
    `${safeString(value).slice(
      0,
      10
    )}T00:00:00`
  );

  if (
    Number.isNaN(date.getTime())
  ) {
    return "—";
  }

  return new Intl.DateTimeFormat(
    "en-US",
    {
      month: "short",
      day: "numeric",
      year: "numeric",
    }
  ).format(date);
}

function formatLabel(value) {
  return safeString(value)
    .replaceAll("_", " ")
    .replace(
      /\b\w/g,
      (letter) =>
        letter.toUpperCase()
    );
}

function getClientName(client) {
  if (!client) {
    return "Unknown client";
  }

  const personalName = [
    client.first_name,
    client.last_name,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    client.business_name ||
    personalName ||
    `Client ${client.id}`
  );
}

function DevelopmentProjects() {
  const [projects, setProjects] =
    useState([]);

  const [clients, setClients] =
    useState([]);

  const [
    selectedProject,
    setSelectedProject,
  ] = useState(null);

  const [formData, setFormData] =
    useState({
      ...EMPTY_FORM,
    });

  const [search, setSearch] =
    useState("");

  const [
    statusFilter,
    setStatusFilter,
  ] = useState("current");

  const [
    clientFilter,
    setClientFilter,
  ] = useState("");

  const [showForm, setShowForm] =
    useState(false);

  const [isLoading, setIsLoading] =
    useState(true);

  const [isSaving, setIsSaving] =
    useState(false);

  const [error, setError] =
    useState("");

  const [success, setSuccess] =
    useState("");

  const loadData = useCallback(
    async () => {
      setIsLoading(true);
      setError("");

      try {
        const [
          projectResponse,
          clientResponse,
        ] = await Promise.all([
          Api.getDevelopmentProjects(),
          Api.getDevelopmentClients(),
        ]);

        setProjects(
          projectResponse?.projects ||
            []
        );

        setClients(
          clientResponse?.clients ||
            []
        );
      } catch (requestError) {
        setError(
          requestError.message ||
            "Projects could not be loaded."
        );
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  const clientMap = useMemo(
    () =>
      new Map(
        clients.map((client) => [
          Number(client.id),
          client,
        ])
      ),
    [clients]
  );

  const filteredProjects =
    useMemo(() => {
      const normalizedSearch =
        search
          .trim()
          .toLowerCase();

      return projects.filter(
        (project) => {
          const archived =
            Number(
              project.archived
            ) === 1;

          const active =
            Number(project.active) ===
            1;

          if (
            statusFilter ===
              "current" &&
            (!active || archived)
          ) {
            return false;
          }

          if (
            statusFilter ===
              "archived" &&
            !archived
          ) {
            return false;
          }

          if (
            statusFilter ===
              "inactive" &&
            (active || archived)
          ) {
            return false;
          }

          if (
            statusFilter.startsWith(
              "status:"
            ) &&
            String(project.status) !==
              statusFilter.replace(
                "status:",
                ""
              )
          ) {
            return false;
          }

          if (
            clientFilter &&
            String(
              project.client_id
            ) !==
              String(clientFilter)
          ) {
            return false;
          }

          if (!normalizedSearch) {
            return true;
          }

          const client =
            clientMap.get(
              Number(
                project.client_id
              )
            );

          const searchableText = [
            project.title,
            project.description,
            project.project_type,
            project.status,
            project.priority,
            project.business_name,
            project.first_name,
            project.last_name,
            getClientName(client),
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

          return searchableText.includes(
            normalizedSearch
          );
        }
      );
    }, [
      projects,
      search,
      statusFilter,
      clientFilter,
      clientMap,
    ]);

  const summary = useMemo(
    () => ({
      total:
        projects.length,

      current:
        projects.filter(
          (project) =>
            Number(
              project.active
            ) === 1 &&
            Number(
              project.archived
            ) !== 1
        ).length,

      completed:
        projects.filter(
          (project) =>
            String(
              project.status
            ) === "completed"
        ).length,

      archived:
        projects.filter(
          (project) =>
            Number(
              project.archived
            ) === 1
        ).length,

      outstanding:
        projects.reduce(
          (total, project) =>
            total +
            (
              Number(
                project.remaining_paid
              ) === 1
                ? 0
                : Number(
                    project.remaining_amount
                  ) || 0
            ),
          0
        ),
    }),
    [projects]
  );

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const openNewProjectForm = () => {
    clearMessages();

    setSelectedProject(null);

    setFormData({
      ...EMPTY_FORM,
      client_id:
        clientFilter || "",
    });

    setShowForm(true);
  };

  const openEditProjectForm = (
    project
  ) => {
    clearMessages();

    setSelectedProject(project);

    setFormData(
      normalizeProject(project)
    );

    setShowForm(true);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const closeForm = () => {
    setSelectedProject(null);

    setFormData({
      ...EMPTY_FORM,
    });

    setShowForm(false);
  };

  const handleChange = (event) => {
    const {
      name,
      value,
      type,
      checked,
    } = event.target;

    setFormData((current) => {
      const next = {
        ...current,

        [name]:
          type === "checkbox"
            ? checked
            : value,
      };

      if (
        name === "archived" &&
        checked
      ) {
        next.active = false;
      }

      if (
        name === "active" &&
        checked
      ) {
        next.archived = false;
      }

      return next;
    });
  };

  const validateForm = () => {
    if (!formData.client_id) {
      return "A client is required.";
    }

    if (
      !cleanText(
        formData.title
      )
    ) {
      return "Project title is required.";
    }

    const totalCost = Number(
      formData.total_cost || 0
    );

    if (
      !Number.isFinite(totalCost) ||
      totalCost < 0
    ) {
      return "Total cost must be 0 or greater.";
    }

    const depositPercentage =
      Number(
        formData.deposit_percentage
      );

    if (
      !Number.isFinite(
        depositPercentage
      ) ||
      depositPercentage < 0 ||
      depositPercentage > 100
    ) {
      return "Deposit percentage must be between 0 and 100.";
    }

    return "";
  };

  const buildPayload = () => ({
    client_id:
      Number(
        formData.client_id
      ),

    title:
      cleanText(
        formData.title
      ),

    description:
      optionalText(
        formData.description
      ),

    project_type:
      cleanText(
        formData.project_type
      ) || "website",

    status:
      cleanText(
        formData.status
      ) || "draft",

    priority:
      cleanText(
        formData.priority
      ) || "normal",

    start_date:
      normalizeDateInput(
        formData.start_date
      ) || null,

    target_completion_date:
      normalizeDateInput(
        formData.target_completion_date
      ) || null,

    actual_completion_date:
      normalizeDateInput(
        formData.actual_completion_date
      ) || null,

    total_cost:
      Number(
        formData.total_cost || 0
      ),

    deposit_percentage:
      Number(
        formData.deposit_percentage ||
          0
      ),

    deposit_paid:
      Boolean(
        formData.deposit_paid
      ),

    remaining_paid:
      Boolean(
        formData.remaining_paid
      ),

    internal_notes:
      optionalText(
        formData.internal_notes
      ),

    portfolio_eligible:
      Boolean(
        formData.portfolio_eligible
      ),

    active:
      Boolean(
        formData.active
      ),

    archived:
      Boolean(
        formData.archived
      ),
  });

  const handleSubmit = async (
    event
  ) => {
    event.preventDefault();

    clearMessages();

    const validationError =
      validateForm();

    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSaving(true);

    try {
      const payload =
        buildPayload();

      if (selectedProject) {
        await Api.updateDevelopmentProject(
          selectedProject.id,
          payload
        );

        setSuccess(
          "Project updated successfully."
        );
      } else {
        await Api.createDevelopmentProject(
          payload
        );

        setSuccess(
          "Project created successfully."
        );
      }

      await loadData();

      closeForm();
    } catch (requestError) {
      setError(
        requestError.message ||
          "The project could not be saved."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const updateProjectState =
    async (
      project,
      changes,
      successMessage
    ) => {
      clearMessages();

      try {
        await Api.updateDevelopmentProject(
          project.id,
          changes
        );

        setSuccess(
          successMessage
        );

        await loadData();
      } catch (requestError) {
        setError(
          requestError.message ||
            "The project could not be updated."
        );
      }
    };

  const archiveProject = async (
    project
  ) => {
    const nextArchived =
      Number(
        project.archived
      ) !== 1;

    const action =
      nextArchived
        ? "archive"
        : "restore";

    const confirmed =
      window.confirm(
        `Are you sure you want to ${action} "${project.title}"?`
      );

    if (!confirmed) {
      return;
    }

    await updateProjectState(
      project,
      {
        archived:
          nextArchived,

        active:
          nextArchived
            ? false
            : true,
      },
      nextArchived
        ? "Project archived."
        : "Project restored."
    );
  };

  return (
    <div className="development-projects-page">
      <div className="development-admin-page-heading">
        <div>
          <p className="development-admin-eyebrow">
            Project management
          </p>

          <h2>Projects</h2>

          <p>
            Manage client projects,
            schedules, financial status,
            priorities, and portfolio
            eligibility.
          </p>
        </div>

        <div className="development-admin-heading-actions">
          <button
            type="button"
            className="development-admin-button primary"
            onClick={
              openNewProjectForm
            }
          >
            Add project
          </button>
        </div>
      </div>

      {error && (
        <div
          className="development-admin-alert error"
          role="alert"
        >
          {error}
        </div>
      )}

      {success && (
        <div
          className="development-admin-alert success"
          role="status"
        >
          {success}
        </div>
      )}

      {showForm && (
        <ProjectForm
          formData={formData}
          clients={clients}
          selectedProject={
            selectedProject
          }
          isSaving={isSaving}
          onChange={handleChange}
          onSubmit={handleSubmit}
          onCancel={closeForm}
        />
      )}

      <section className="development-project-summary-grid">
        <SummaryCard
          label="All projects"
          value={summary.total}
          active={
            statusFilter === "all"
          }
          onClick={() =>
            setStatusFilter("all")
          }
        />

        <SummaryCard
          label="Current"
          value={summary.current}
          active={
            statusFilter ===
            "current"
          }
          onClick={() =>
            setStatusFilter(
              "current"
            )
          }
        />

        <SummaryCard
          label="Completed"
          value={summary.completed}
          active={
            statusFilter ===
            "status:completed"
          }
          onClick={() =>
            setStatusFilter(
              "status:completed"
            )
          }
        />

        <SummaryCard
          label="Archived"
          value={summary.archived}
          active={
            statusFilter ===
            "archived"
          }
          onClick={() =>
            setStatusFilter(
              "archived"
            )
          }
        />

        <SummaryCard
          label="Outstanding"
          value={formatCurrency(
            summary.outstanding
          )}
          active={false}
          onClick={() =>
            setStatusFilter(
              "current"
            )
          }
        />
      </section>

      <section className="development-projects-panel">
        <div className="development-projects-toolbar">
          <div>
            <h3>
              Project records
            </h3>

            <p>
              {filteredProjects.length}{" "}
              project
              {filteredProjects.length ===
              1
                ? ""
                : "s"}{" "}
              shown
            </p>
          </div>

          <div className="development-project-filters">
            <input
              type="search"
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
              placeholder="Search projects..."
              aria-label="Search projects"
            />

            <select
              value={clientFilter}
              onChange={(event) =>
                setClientFilter(
                  event.target.value
                )
              }
              aria-label="Filter by client"
            >
              <option value="">
                All clients
              </option>

              {clients.map(
                (client) => (
                  <option
                    key={client.id}
                    value={client.id}
                  >
                    {getClientName(
                      client
                    )}
                  </option>
                )
              )}
            </select>

            <select
              value={statusFilter}
              onChange={(event) =>
                setStatusFilter(
                  event.target.value
                )
              }
              aria-label="Filter project status"
            >
              <option value="all">
                All projects
              </option>

              <option value="current">
                Current projects
              </option>

              <option value="inactive">
                Inactive projects
              </option>

              <option value="archived">
                Archived projects
              </option>

              {STATUS_OPTIONS.map(
                (status) => (
                  <option
                    key={status}
                    value={`status:${status}`}
                  >
                    {formatLabel(
                      status
                    )}
                  </option>
                )
              )}
            </select>
          </div>
        </div>

        {isLoading ? (
          <div
            className="development-admin-state"
            role="status"
          >
            Loading projects...
          </div>
        ) : filteredProjects.length ===
          0 ? (
          <div className="development-admin-empty">
            No projects match the
            current filters.
          </div>
        ) : (
          <div className="development-project-card-grid">
            {filteredProjects.map(
              (project) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  client={clientMap.get(
                    Number(
                      project.client_id
                    )
                  )}
                  onEdit={() =>
                    openEditProjectForm(
                      project
                    )
                  }
                  onArchive={() =>
                    archiveProject(
                      project
                    )
                  }
                  onStatusChange={(
                    status
                  ) =>
                    updateProjectState(
                      project,
                      {
                        status,
                      },
                      "Project status updated."
                    )
                  }
                />
              )
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  active,
  onClick,
}) {
  return (
    <button
      type="button"
      className={`development-project-summary-card${
        active ? " active" : ""
      }`}
      onClick={onClick}
    >
      <span>{label}</span>

      <strong>{value}</strong>
    </button>
  );
}

function ProjectCard({
  project,
  client,
  onEdit,
  onArchive,
  onStatusChange,
}) {
  const archived =
    Number(project.archived) === 1;

  return (
    <article
      className={`development-project-card${
        archived
          ? " archived"
          : ""
      }`}
    >
      <div className="development-project-card-header">
        <div>
          <p className="development-project-client-name">
            {getClientName(client)}
          </p>

          <h4>{project.title}</h4>
        </div>

        <span
          className={`development-project-priority ${
            project.priority ||
            "normal"
          }`}
        >
          {formatLabel(
            project.priority ||
              "normal"
          )}
        </span>
      </div>

      <p className="development-project-description">
        {project.description ||
          "No project description has been added."}
      </p>

      <div className="development-project-meta-grid">
        <div>
          <span>Type</span>

          <strong>
            {formatLabel(
              project.project_type ||
                "other"
            )}
          </strong>
        </div>

        <div>
          <span>Total cost</span>

          <strong>
            {formatCurrency(
              project.total_cost
            )}
          </strong>
        </div>

        <div>
          <span>Deposit</span>

          <strong>
            {formatCurrency(
              project.deposit_amount
            )}
          </strong>
        </div>

        <div>
          <span>Remaining</span>

          <strong>
            {formatCurrency(
              project.remaining_amount
            )}
          </strong>
        </div>

        <div>
          <span>Start date</span>

          <strong>
            {formatDate(
              project.start_date
            )}
          </strong>
        </div>

        <div>
          <span>Target</span>

          <strong>
            {formatDate(
              project.target_completion_date
            )}
          </strong>
        </div>
      </div>

      <div className="development-project-payment-flags">
        <PaymentFlag
          label="Deposit paid"
          active={
            Number(
              project.deposit_paid
            ) === 1
          }
        />

        <PaymentFlag
          label="Remaining paid"
          active={
            Number(
              project.remaining_paid
            ) === 1
          }
        />

        <PaymentFlag
          label="Fully paid"
          active={
            Number(
              project.fully_paid
            ) === 1
          }
        />
      </div>

      <div className="development-project-card-footer">
        <select
          value={
            project.status ||
            "draft"
          }
          onChange={(event) =>
            onStatusChange(
              event.target.value
            )
          }
          aria-label={`Update status for ${project.title}`}
          disabled={archived}
        >
          {STATUS_OPTIONS.map(
            (status) => (
              <option
                key={status}
                value={status}
              >
                {formatLabel(
                  status
                )}
              </option>
            )
          )}
        </select>

        <div className="development-project-card-actions">
          <button
            type="button"
            onClick={onEdit}
          >
            Edit
          </button>

          <button
            type="button"
            className={
              archived
                ? "restore"
                : "archive"
            }
            onClick={onArchive}
          >
            {archived
              ? "Restore"
              : "Archive"}
          </button>
        </div>
      </div>
    </article>
  );
}

function PaymentFlag({
  label,
  active,
}) {
  return (
    <span
      className={`development-project-payment-flag${
        active
          ? " paid"
          : ""
      }`}
    >
      {active ? "✓" : "○"}{" "}
      {label}
    </span>
  );
}

function ProjectForm({
  formData,
  clients,
  selectedProject,
  isSaving,
  onChange,
  onSubmit,
  onCancel,
}) {
  const totalCost =
    Number(
      formData.total_cost || 0
    ) || 0;

  const depositPercentage =
    Number(
      formData.deposit_percentage ||
        0
    ) || 0;

  const depositAmount =
    totalCost *
    (depositPercentage / 100);

  const remainingAmount =
    totalCost - depositAmount;

  const fullyPaid =
    Boolean(
      formData.deposit_paid
    ) &&
    Boolean(
      formData.remaining_paid
    );

  return (
    <section className="development-project-form-panel">
      <div className="development-project-form-heading">
        <div>
          <p className="development-admin-eyebrow">
            {selectedProject
              ? "Edit project"
              : "New project"}
          </p>

          <h3>
            {selectedProject
              ? selectedProject.title
              : "Create project"}
          </h3>
        </div>

        <button
          type="button"
          className="development-project-close-button"
          onClick={onCancel}
          aria-label="Close project form"
        >
          ×
        </button>
      </div>

      <form
        className="development-project-form"
        onSubmit={onSubmit}
      >
        <div className="development-project-form-section">
          <h4>
            Project information
          </h4>

          <div className="development-project-form-grid">
            <label className="development-project-field">
              <span>
                Client
                <span className="required-marker">
                  *
                </span>
              </span>

              <select
                name="client_id"
                value={
                  safeString(
                    formData.client_id
                  )
                }
                onChange={onChange}
                required
              >
                <option value="">
                  Select client
                </option>

                {clients.map(
                  (client) => (
                    <option
                      key={client.id}
                      value={client.id}
                    >
                      {getClientName(
                        client
                      )}
                    </option>
                  )
                )}
              </select>
            </label>

            <FormField
              label="Project title"
              name="title"
              value={formData.title}
              onChange={onChange}
              required
            />

            <label className="development-project-field">
              <span>
                Project type
              </span>

              <select
                name="project_type"
                value={
                  safeString(
                    formData.project_type
                  ) || "website"
                }
                onChange={onChange}
              >
                {PROJECT_TYPE_OPTIONS.map(
                  (type) => (
                    <option
                      key={type}
                      value={type}
                    >
                      {formatLabel(
                        type
                      )}
                    </option>
                  )
                )}
              </select>
            </label>

            <label className="development-project-field">
              <span>Status</span>

              <select
                name="status"
                value={
                  safeString(
                    formData.status
                  ) || "draft"
                }
                onChange={onChange}
              >
                {STATUS_OPTIONS.map(
                  (status) => (
                    <option
                      key={status}
                      value={status}
                    >
                      {formatLabel(
                        status
                      )}
                    </option>
                  )
                )}
              </select>
            </label>

            <label className="development-project-field">
              <span>Priority</span>

              <select
                name="priority"
                value={
                  safeString(
                    formData.priority
                  ) || "normal"
                }
                onChange={onChange}
              >
                {PRIORITY_OPTIONS.map(
                  (priority) => (
                    <option
                      key={priority}
                      value={priority}
                    >
                      {formatLabel(
                        priority
                      )}
                    </option>
                  )
                )}
              </select>
            </label>

            <label className="development-project-field full-width">
              <span>Description</span>

              <textarea
                name="description"
                value={
                  safeString(
                    formData.description
                  )
                }
                onChange={onChange}
                rows="4"
              />
            </label>
          </div>
        </div>

        <div className="development-project-form-section">
          <h4>Schedule</h4>

          <div className="development-project-form-grid three-columns">
            <FormField
              label="Start date"
              name="start_date"
              type="date"
              value={
                formData.start_date
              }
              onChange={onChange}
            />

            <FormField
              label="Target completion"
              name="target_completion_date"
              type="date"
              value={
                formData.target_completion_date
              }
              onChange={onChange}
            />

            <FormField
              label="Actual completion"
              name="actual_completion_date"
              type="date"
              value={
                formData.actual_completion_date
              }
              onChange={onChange}
            />
          </div>
        </div>

        <div className="development-project-form-section">
          <h4>Financials</h4>

          <div className="development-project-form-grid">
            <FormField
              label="Total cost"
              name="total_cost"
              type="number"
              value={
                formData.total_cost
              }
              onChange={onChange}
              min="0"
              step="0.01"
            />

            <FormField
              label="Deposit percentage"
              name="deposit_percentage"
              type="number"
              value={
                formData.deposit_percentage
              }
              onChange={onChange}
              min="0"
              max="100"
              step="0.01"
            />
          </div>

          <div className="development-project-financial-preview">
            <div>
              <span>
                Deposit amount
              </span>

              <strong>
                {formatCurrency(
                  depositAmount
                )}
              </strong>
            </div>

            <div>
              <span>
                Remaining amount
              </span>

              <strong>
                {formatCurrency(
                  remainingAmount
                )}
              </strong>
            </div>
          </div>

          <div className="development-project-checkbox-grid">
            <CheckboxField
              name="deposit_paid"
              label="Deposit paid"
              checked={
                formData.deposit_paid
              }
              onChange={onChange}
            />

            <CheckboxField
              name="remaining_paid"
              label="Remaining balance paid"
              checked={
                formData.remaining_paid
              }
              onChange={onChange}
            />

            <CalculatedCheckbox
              label="Project fully paid"
              checked={fullyPaid}
            />
          </div>
        </div>

        <div className="development-project-form-section">
          <h4>
            Administrative settings
          </h4>

          <label className="development-project-field full-width">
            <span>
              Internal notes
            </span>

            <textarea
              name="internal_notes"
              value={
                safeString(
                  formData.internal_notes
                )
              }
              onChange={onChange}
              rows="5"
            />
          </label>

          <div className="development-project-checkbox-grid">
            <CheckboxField
              name="portfolio_eligible"
              label="Eligible for portfolio"
              checked={
                formData.portfolio_eligible
              }
              onChange={onChange}
            />

            <CheckboxField
              name="active"
              label="Project is active"
              checked={
                formData.active
              }
              onChange={onChange}
            />

            <CheckboxField
              name="archived"
              label="Project is archived"
              checked={
                formData.archived
              }
              onChange={onChange}
            />
          </div>
        </div>

        <div className="development-project-form-actions">
          <button
            type="button"
            className="development-admin-button secondary"
            onClick={onCancel}
            disabled={isSaving}
          >
            Cancel
          </button>

          <button
            type="submit"
            className="development-admin-button primary"
            disabled={isSaving}
          >
            {isSaving
              ? "Saving..."
              : selectedProject
                ? "Save changes"
                : "Create project"}
          </button>
        </div>
      </form>
    </section>
  );
}

function FormField({
  label,
  name,
  type = "text",
  value,
  onChange,
  required = false,
  min,
  max,
  step,
}) {
  return (
    <label className="development-project-field">
      <span>
        {label}

        {required && (
          <span className="required-marker">
            *
          </span>
        )}
      </span>

      <input
        type={type}
        name={name}
        value={safeString(value)}
        onChange={onChange}
        required={required}
        min={min}
        max={max}
        step={step}
      />
    </label>
  );
}

function CheckboxField({
  name,
  label,
  checked,
  onChange,
}) {
  return (
    <label className="development-project-checkbox">
      <input
        type="checkbox"
        name={name}
        checked={Boolean(checked)}
        onChange={onChange}
      />

      <span>{label}</span>
    </label>
  );
}

function CalculatedCheckbox({
  label,
  checked,
}) {
  return (
    <label className="development-project-checkbox">
      <input
        type="checkbox"
        checked={Boolean(checked)}
        readOnly
        disabled
      />

      <span>
        {label} (calculated)
      </span>
    </label>
  );
}

export default DevelopmentProjects;